'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Shipment {
  id: string;
  trackingNumber: string;
  origin: string;
  destination: string;
  status: string;
  savedDate: string;
  estimatedDelivery: string;
}

const mockShipments: Shipment[] = [
  {
    id: '1',
    trackingNumber: 'SHP-2026-001234',
    origin: 'New York, USA',
    destination: 'London, UK',
    status: 'In Transit',
    savedDate: '2026-02-01',
    estimatedDelivery: '2026-02-10',
  },
  {
    id: '2',
    trackingNumber: 'SHP-2026-005678',
    origin: 'Los Angeles, USA',
    destination: 'Tokyo, Japan',
    status: 'Delivered',
    savedDate: '2026-01-28',
    estimatedDelivery: '2026-02-05',
  },
  {
    id: '3',
    trackingNumber: 'SHP-2026-009012',
    origin: 'Chicago, USA',
    destination: 'Paris, France',
    status: 'Pending',
    savedDate: '2026-02-05',
    estimatedDelivery: '2026-02-15',
  },
];

export default function SavedShipments() {
  const [shipments] = useState<Shipment[]>(mockShipments);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (trackingNumber: string, id: string) => {
    navigator.clipboard.writeText(trackingNumber);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'in transit':
        return 'bg-warning/10 text-warning';
      case 'pending':
        return 'bg-muted text-muted-foreground';
      case 'delayed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Saved Shipments ({shipments.length})
        </h2>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          <Icon name="PlusIcon" size={16} />
          Add Shipment
        </button>
      </div>

      {/* Shipments Grid */}
      {shipments.length > 0 ? (
        <div className="grid gap-6">
          {shipments.map((shipment) => (
            <div
              key={shipment.id}
              className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-muted-foreground">Tracking #:</span>
                    <span className="text-sm font-mono font-medium text-foreground">
                      {shipment.trackingNumber}
                    </span>
                    <button
                      onClick={() => handleCopy(shipment.trackingNumber, shipment.id)}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title="Copy tracking number"
                    >
                      <Icon
                        name={copiedId === shipment.id ? 'CheckIcon' : 'ClipboardDocumentIcon'}
                        size={16}
                        className={copiedId === shipment.id ? 'text-success' : 'text-muted-foreground'}
                      />
                    </button>
                  </div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(shipment.status)}`}>
                    {shipment.status}
                  </span>
                </div>
                <button className="p-2 hover:bg-muted rounded-lg transition-colors" title="Remove shipment">
                  <Icon name="TrashIcon" size={20} className="text-muted-foreground hover:text-error" />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Icon name="MapPinIcon" size={16} />
                    <span>Origin</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{shipment.origin}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Icon name="MapPinIcon" size={16} />
                    <span>Destination</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{shipment.destination}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Icon name="CalendarIcon" size={16} />
                    <span>Saved Date</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{shipment.savedDate}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Icon name="ClockIcon" size={16} />
                    <span>Est. Delivery</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{shipment.estimatedDelivery}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border flex gap-3">
                <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                  <Icon name="MagnifyingGlassIcon" size={16} />
                  Track Shipment
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors">
                  <Icon name="ArrowPathIcon" size={16} />
                  Reship
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl p-12 text-center border border-border">
          <Icon name="ArchiveBoxIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No saved shipments yet</p>
        </div>
      )}
    </div>
  );
}